import React, { useState } from "react";
import { useBranding } from "@/contexts/BrandingContext";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CirclePlus, Palette, Save, Image as ImageIcon } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";

export function BrandingSettings() {
  const { branding, updateBranding } = useBranding();
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({...branding, logoUrl: undefined});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const saveChanges = () => {
    updateBranding(formData);
    toast({
      title: "Branding Updated",
      description: "Your branding changes have been applied.",
    });
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-xl font-semibold">Company Branding</CardTitle>
        <CardDescription>
          Customize the look and feel of your application
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="general">
          <TabsList className="mb-4">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="colors">Colors</TabsTrigger>
            <TabsTrigger value="advanced">Advanced</TabsTrigger>
          </TabsList>
          
          <TabsContent value="general" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">App Name</Label>
              <Input 
                id="name" 
                name="name"
                value={formData.name} 
                onChange={handleChange} 
                placeholder="F.F.V"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="companyName">Company Name</Label>
              <Input 
                id="companyName" 
                name="companyName"
                value={formData.companyName || ''} 
                onChange={handleChange} 
                placeholder="Your Company Name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="logo">Company Logo</Label>
              <div className="flex items-center justify-center border rounded-md overflow-hidden bg-gray-50 h-20 w-20">
                <ImageIcon className="h-8 w-8 text-gray-400" />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="fontFamily">Font Family</Label>
              <Input 
                id="fontFamily" 
                name="fontFamily"
                value={formData.fontFamily} 
                onChange={handleChange} 
                placeholder="Inter, system-ui, sans-serif"
              />
              <p className="text-xs text-muted-foreground">
                Specify comma-separated font family names
              </p>
            </div>
          </TabsContent>
          
          <TabsContent value="colors" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="primaryColor" className="flex items-center">
                  <Palette className="h-4 w-4 mr-2" />
                  Primary Color
                </Label>
                <div className="flex items-center gap-2">
                  <div 
                    className="h-8 w-8 rounded-full border"
                    style={{ backgroundColor: formData.primaryColor }}
                  />
                  <Input 
                    id="primaryColor" 
                    name="primaryColor"
                    value={formData.primaryColor} 
                    onChange={handleChange} 
                    placeholder="#3b82f6"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="secondaryColor" className="flex items-center">
                  <Palette className="h-4 w-4 mr-2" />
                  Secondary Color
                </Label>
                <div className="flex items-center gap-2">
                  <div 
                    className="h-8 w-8 rounded-full border"
                    style={{ backgroundColor: formData.secondaryColor }}
                  />
                  <Input 
                    id="secondaryColor" 
                    name="secondaryColor"
                    value={formData.secondaryColor} 
                    onChange={handleChange} 
                    placeholder="#10b981"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="accentColor" className="flex items-center">
                  <Palette className="h-4 w-4 mr-2" />
                  Accent Color
                </Label>
                <div className="flex items-center gap-2">
                  <div 
                    className="h-8 w-8 rounded-full border"
                    style={{ backgroundColor: formData.accentColor }}
                  />
                  <Input 
                    id="accentColor" 
                    name="accentColor"
                    value={formData.accentColor} 
                    onChange={handleChange} 
                    placeholder="#8b5cf6"
                  />
                </div>
              </div>
            </div>
            
            <div className="mt-4 p-4 rounded-md bg-gray-50">
              <h4 className="font-medium mb-2">Preview</h4>
              <div className="flex gap-2">
                <Button style={{ backgroundColor: formData.primaryColor }}>
                  Primary
                </Button>
                <Button style={{ backgroundColor: formData.secondaryColor }}>
                  Secondary
                </Button>
                <Button style={{ backgroundColor: formData.accentColor }}>
                  Accent
                </Button>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="advanced">
            <div className="space-y-2">
              <Label htmlFor="customCss">Custom CSS</Label>
              <Textarea 
                id="customCss" 
                name="customCss"
                value={formData.customCss || ""} 
                onChange={handleChange}
                placeholder=".custom-class { color: var(--primary-color); }"
                className="font-mono text-sm h-32"
              />
              <p className="text-xs text-muted-foreground">
                Add custom CSS to further customize your instance.
                Use variables like <code>var(--primary-color)</code>, <code>var(--secondary-color)</code>, and <code>var(--accent-color)</code>.
              </p>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
      <CardFooter>
        <Button onClick={saveChanges} className="w-full sm:w-auto">
          <Save className="h-4 w-4 mr-2" />
          Save Branding Changes
        </Button>
      </CardFooter>
    </Card>
  );
}
